import { useEffect, useRef } from "react";

import { emitSearch } from "~/hec/analytics/event-layer/gtm-search-event";

import { trackPageEvent } from "./utils";

// SFCC-11014 — page-level Segment events for product lists. View Category rides
// the SFCC-11013 tile-impression endpoint (see `useTileImpressions`); this file
// owns the search-only `Products Searched` event, which needs no server data.

/**
 * Fires Segment `Products Searched` once per non-empty search query. Mirrors
 * SFRA `segmentLayerHelper.searchSegmentDataLayerViewHelper` (:75-93): the
 * `isSearch` guard (`:77`) means an empty/absent query fires nothing, and it
 * fires once per Search-Show — not on filter / sort / pagination (those hit
 * `Search-UpdateGrid` in SFRA, here a same-query loader revalidation, which the
 * per-query dedup ignores). `device` + `sales_channel` match the
 * <SegmentIdentify /> trailer.
 */
export const useTrackProductsSearched = (
  query: string | undefined,
  siteId: string,
): void => {
  const firedFor = useRef<string | null>(null);

  useEffect(() => {
    const q = (query ?? "").trim();
    if (!q) return; // empty/null query → no event (SFRA `isSearch` guard)
    if (firedFor.current === q) return;
    // Set synchronously before tracking so a concurrent effect re-run for the
    // same query can't fire a second event. trackPageEvent owns the ready-wait +
    // `device`/`sales_channel` decoration.
    firedFor.current = q;
    // GTM GA4_search fork — dormant until martech adds the container trigger;
    // the always-on Segment search event is "Products Searched" below.
    emitSearch(q);
    return trackPageEvent("Products Searched", { query: q }, siteId);
  }, [query, siteId]);
};
